import React from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { SITE_CONFIG } from '../../constants/siteConfig.js';
import WebTopBar from '../components/WebTopBar.js';

/**
 * Centro de ayuda: consejos y preguntas frecuentes por área.
 * Contenido orientado al usuario final, sin detalles técnicos internos.
 */
const FAQ_SECTIONS = [
  { id: 'estado-beta', title: 'Estado de la beta (L1)' },
  { id: 'mercado', title: 'Mercado' },
  { id: 'ficha', title: 'Ficha de anuncio' },
  { id: 'chat', title: 'Chat' },
  { id: 'publicar', title: 'Publicar anuncio' },
  { id: 'grooves', title: 'Grooves (moneda interna)' },
  { id: 'perfil', title: 'Tu perfil y colección' },
  { id: 'pedidos-ventas', title: 'Pedidos y ventas' },
  { id: 'valoraciones', title: 'Valoraciones' },
  { id: 'notificaciones', title: 'Notificaciones' },
  { id: 'ajustes', title: 'Ajustes de la cuenta' },
  { id: 'comunidad', title: 'Comunidad' },
  { id: 'subastas', title: 'Subastas' },
  { id: 'partners', title: 'Partners verificados' },
  { id: 'noticias', title: 'Noticias' },
  { id: 'perfil-vendedor', title: 'Perfil de vendedor' },
  { id: 'soporte', title: 'Soporte y errores' },
];

const detailsStyle = {
  marginBottom: 10,
  borderRadius: 10,
  border: '1px solid rgba(255,255,255,0.1)',
  background: 'rgba(255,255,255,0.03)',
  overflow: 'hidden',
};

const summaryStyle = {
  padding: '14px 16px',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: '0.92rem',
  color: 'rgba(255,255,255,0.92)',
  listStyle: 'none',
};

const bodyStyle = {
  padding: '12px 16px 16px',
  borderTop: '1px solid rgba(255,255,255,0.06)',
  color: 'rgba(255,255,255,0.72)',
  fontSize: '0.86rem',
  lineHeight: 1.6,
};

const hStyle = { fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(200,168,75,0.9)', fontWeight: 900, margin: '10px 0 6px' };
const pMuted = { color: 'rgba(255,255,255,0.55)', margin: '6px 0 0' };
const ulStyle = { margin: '6px 0 0', paddingLeft: 18 };
const liStyle = { margin: '6px 0' };

function HelpCTA({ label, onClick, kind = 'gold' }) {
  const base = {
    border: 'none',
    cursor: 'pointer',
    fontWeight: 900,
    borderRadius: 10,
    padding: '10px 12px',
    fontSize: 12,
    letterSpacing: '0.02em',
  };
  const styles =
    kind === 'ghost'
      ? { ...base, background: 'transparent', color: C.gold, border: `1px solid rgba(200,168,75,0.45)` }
      : { ...base, background: C.gold, color: '#0a0a0f' };
  return (
    <button type="button" onClick={onClick} style={styles}>
      {label}
    </button>
  );
}

export default function PageCentroAyuda({ onBack, onNavigate, user }) {
  const go = (p, o) => {
    if (typeof onNavigate === 'function') onNavigate(p, o);
  };

  return (
    <div style={{ background: '#0a0a0f', minHeight: '100vh', color: '#fff' }}>
      <WebTopBar onBack={onBack} title="Centro de ayuda" mini dark user={user} onNavigate={onNavigate} onSignOut={onBack} />
      <article style={{ maxWidth: 720, margin: '0 auto', padding: '28px 20px 56px' }}>
        <h1 style={{ color: C.gold, fontSize: '1.45rem', fontWeight: 900, margin: '0 0 8px', letterSpacing: '-0.03em', lineHeight: 1.25 }}>
          Centro de ayuda
        </h1>
        <p style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.45)', marginBottom: 22 }}>
          Preguntas frecuentes · {SITE_CONFIG.nombre}
        </p>
        <div>
          {FAQ_SECTIONS.map(({ id, title }) => (
            <details key={id} id={`ayuda-${id}`} style={detailsStyle}>
              <summary style={summaryStyle}>{title}</summary>
              <div style={bodyStyle}>
                {id === 'estado-beta' ? (
                  <>
                    <div style={hStyle}>Resumen</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>Objetivo:</strong> probar Mercado, Comunidad, Noticias, Publicación y Chat.</li>
                      <li style={liStyle}><strong>Venta simple:</strong> los acuerdos se realizan por <strong>chat</strong> (entrega en mano o envío entre usuarios).</li>
                      <li style={liStyle}><strong>Grooves:</strong> puedes ganar y gastar Grooves (si tienes email + teléfono verificados) para funciones internas como IA y sorteos cuando apliquen.</li>
                    </ul>
                    <p style={pMuted}>
                      Algunas funciones avanzadas se irán activando progresivamente. Si algo no aparece o no funciona como esperas, repórtalo desde soporte.
                    </p>
                  </>
                ) : null}

                {id === 'mercado' ? (
                  <>
                    <div style={hStyle}>Consejos</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>Busca y filtra:</strong> usa la lupa y los filtros para acotar por género, formato, estado y ubicación.</li>
                      <li style={liStyle}><strong>Ordena el feed:</strong> alterna entre <em>Novedades</em> y <em>Recomendaciones</em> desde la barra de resultados.</li>
                      <li style={liStyle}><strong>Favoritos:</strong> el corazón guarda anuncios en tu lista (requiere sesión).</li>
                      <li style={liStyle}><strong>Modo invitado:</strong> puedes explorar; al intentar interactuar (favoritos, publicar, chat) la app te pedirá registrarte/iniciar sesión.</li>
                    </ul>
                    <div style={hStyle}>Preguntas frecuentes</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>¿Qué puedo hacer en el Mercado?</strong> Explorar anuncios publicados por la comunidad y entrar en la ficha de cada disco.</li>
                      <li style={liStyle}><strong>¿Necesito cuenta para ver discos?</strong> Para entrar a la beta necesitas invitación. Una vez dentro puedes explorar sin cuenta, pero para guardar favoritos o chatear necesitas sesión.</li>
                      <li style={liStyle}><strong>¿Cómo guardo un disco que me interesa?</strong> Pulsa el corazón en una tarjeta o en la ficha.</li>
                    </ul>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      <HelpCTA label="Abrir Mercado" onClick={() => go('marketplace')} />
                      <HelpCTA label="Abrir filtros" kind="ghost" onClick={() => go('marketplace')} />
                    </div>
                  </>
                ) : null}

                {id === 'ficha' ? (
                  <>
                    <div style={hStyle}>Guía rápida</div>
                    <ol style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                      <li style={liStyle}>Revisa <strong>fotos</strong>, estado y descripción del anuncio.</li>
                      <li style={liStyle}>Mira el <strong>perfil del vendedor</strong> (valoraciones y más anuncios).</li>
                      <li style={liStyle}>Si te interesa, pulsa <strong>Enviar mensaje al vendedor</strong> para acordar detalles.</li>
                    </ol>
                    <div style={hStyle}>Preguntas frecuentes</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>¿Cómo contacto con el vendedor?</strong> Pulsa <strong>Enviar mensaje al vendedor</strong> en la ficha.</li>
                      <li style={liStyle}><strong>¿Qué debo preguntar?</strong> Estado real, edición si aplica, y forma de entrega (en mano o envío) antes de cerrar nada.</li>
                    </ul>
                  </>
                ) : null}

                {id === 'chat' ? (
                  <>
                    <div style={hStyle}>Consejos</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>Chat desde un anuncio:</strong> abre una conversación con contexto (anuncio, precio y vendedor).</li>
                      <li style={liStyle}><strong>Respeto y seguridad:</strong> evita compartir datos sensibles y usa un tono claro; el sistema puede ocultar mensajes por moderación.</li>
                      <li style={liStyle}><strong>Organización:</strong> cada vendedor/anuncio mantiene su hilo; así no mezclas conversaciones.</li>
                    </ul>
                    <div style={hStyle}>Preguntas frecuentes</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>¿Mis mensajes son privados?</strong> Sí, cada conversación es privada entre las personas del chat.</li>
                      <li style={liStyle}><strong>¿Cómo se cierra una venta en L1?</strong> Acordad por chat el precio final, la entrega (en mano o envío) y las condiciones.</li>
                    </ul>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      <HelpCTA label="Abrir Chat" onClick={() => go('chat')} />
                    </div>
                  </>
                ) : null}

                {id === 'publicar' ? (
                  <>
                    <div style={hStyle}>Publicar (venta simple + chat)</div>
                    <div style={pMuted}>Consejos</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>Fotos:</strong> sube 3 fotos claras (portada, contraportada, detalles). Menos dudas = más respuestas.</li>
                      <li style={liStyle}><strong>Estado real:</strong> describe honestamente el estado del disco y la funda.</li>
                      <li style={liStyle}><strong>Precio:</strong> compáralo con anuncios similares y ajusta según edición/estado.</li>
                      <li style={liStyle}><strong>Cierre por chat:</strong> en esta fase la venta se acuerda por chat (entrega/envío entre vosotros).</li>
                    </ul>
                    <div style={hStyle}>IA y Grooves (opcional)</div>
                    <p style={{ margin: 0 }}>
                      Puedes usar análisis IA para sugerir información del anuncio (siempre revísala y corrígela). Para <strong>gastar Grooves</strong> hace falta tener <strong>email y teléfono verificados</strong> en Ajustes.
                    </p>
                    <div style={hStyle}>Preguntas frecuentes</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>¿Cómo publico un disco?</strong> Pulsa <strong>+ Vender</strong>, completa los datos y publica.</li>
                      <li style={liStyle}><strong>¿Puedo publicar conjuntos o ediciones especiales?</strong> Sí, si te aparecen esas opciones en el formulario, actívalas y añade los detalles.</li>
                    </ul>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      <HelpCTA label="Ir a Publicar" onClick={() => go('publicar')} />
                      <HelpCTA label="Ajustes → Cuenta" kind="ghost" onClick={() => go('ajustes', { initialSub: 'cuenta' })} />
                    </div>
                  </>
                ) : null}

                {id === 'grooves' ? (
                  <>
                    <div style={hStyle}>Qué son</div>
                    <p style={{ margin: 0 }}>
                      Los <strong>Grooves</strong> son la moneda interna de {SITE_CONFIG.nombre}. Sirven para desbloquear funciones (por ejemplo, <strong>escaneos IA</strong>) y para participar en <strong>sorteos</strong> cuando tengan coste en Grooves.
                    </p>
                    <div style={hStyle}>Activación</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>Email verificado:</strong> puedes ver el saldo (normalmente 0).</li>
                      <li style={liStyle}><strong>Email + teléfono verificados:</strong> puedes <strong>ganar</strong> y <strong>gastar</strong> Grooves.</li>
                    </ul>
                    <div style={hStyle}>En esta fase</div>
                    <p style={{ margin: 0 }}>
                      Puedes conseguir Grooves por objetivos/actividad y usarlos en la plataforma. Algunas opciones se irán ampliando progresivamente.
                    </p>
                    <div style={hStyle}>Preguntas frecuentes</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>¿Por qué mi saldo está en 0?</strong> Revisa Ajustes → Cuenta y verifica email/teléfono; después completa tu perfil y participa en la app.</li>
                      <li style={liStyle}><strong>¿Para qué sirven ahora mismo?</strong> Principalmente para funciones internas como IA y para sorteos cuando apliquen.</li>
                    </ul>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      <HelpCTA label="Ajustes → Verificar teléfono" onClick={() => go('ajustes', { initialSub: 'cuenta' })} />
                      <HelpCTA label="Ir a Comunidad (sorteos)" kind="ghost" onClick={() => go('comunidad')} />
                    </div>
                  </>
                ) : null}

                {id === 'perfil' ? (
                  <>
                    <div style={hStyle}>Consejos</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>Colección:</strong> añade discos a tu colección (no es un anuncio; es tu biblioteca personal).</li>
                      <li style={liStyle}><strong>Joyas / Ofertas:</strong> marca piezas especiales o abiertas a ofertas para destacarlas.</li>
                      <li style={liStyle}><strong>Ubicación:</strong> ayuda a mostrar anuncios cerca y a sugerir opciones al publicar.</li>
                    </ul>
                    <div style={hStyle}>Preguntas frecuentes</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>¿Qué ve el resto de usuarios en mi perfil?</strong> Depende de tu configuración de privacidad en Ajustes.</li>
                      <li style={liStyle}><strong>¿Cómo verifico mi teléfono?</strong> Ajustes → Cuenta → introduce tu número → SMS → confirma el código.</li>
                    </ul>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      <HelpCTA label="Ir a Perfil" onClick={() => go('perfil')} />
                      <HelpCTA label="Ajustes" kind="ghost" onClick={() => go('ajustes')} />
                    </div>
                  </>
                ) : null}

                {id === 'pedidos-ventas' ? (
                  <>
                    <div style={hStyle}>En esta fase (L1)</div>
                    <p style={{ margin: 0 }}>
                      Las compras dentro de la plataforma aún no están activas. Aun así, puedes usar <strong>Mis ventas</strong> para organizar tus anuncios y gestionar interesados por <strong>chat</strong>.
                    </p>
                    <div style={hStyle}>Tips de venta simple</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}>Acordad por chat el <strong>método de entrega</strong> (mano/envío) y el <strong>precio final</strong>.</li>
                      <li style={liStyle}>Si haces envío, comparte <strong>número de seguimiento</strong> cuando lo tengas.</li>
                    </ul>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      <HelpCTA label="Ver Mis ventas" onClick={() => go('mis-ventas')} />
                      <HelpCTA label="Abrir Chat" kind="ghost" onClick={() => go('chat')} />
                    </div>
                  </>
                ) : null}

                {id === 'valoraciones' ? (
                  <>
                    <div style={hStyle}>Qué son</div>
                    <p style={{ margin: 0 }}>
                      Las valoraciones ayudan a construir confianza en la comunidad. Están pensadas para estar ligadas a una transacción real.
                    </p>
                    <div style={hStyle}>En esta fase</div>
                    <p style={{ margin: 0 }}>
                      Si no ves opciones de valorar, es normal: se activarán cuando estén habilitadas las compras dentro de la plataforma.
                    </p>
                  </>
                ) : null}

                {id === 'notificaciones' ? (
                  <>
                    <div style={hStyle}>Consejos</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>Campana:</strong> revisa avisos importantes (mensajes, estado de solicitudes, recordatorios).</li>
                      <li style={liStyle}><strong>Orden:</strong> puedes marcar como leídas y limpiar notificaciones antiguas.</li>
                    </ul>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      <HelpCTA label="Abrir Notificaciones" onClick={() => go('notificaciones')} />
                    </div>
                  </>
                ) : null}

                {id === 'ajustes' ? (
                  <>
                    <div style={hStyle}>Lo más importante</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>Verifica email y teléfono:</strong> es necesario para activar Grooves y para publicar en Comunidad.</li>
                      <li style={liStyle}><strong>Privacidad:</strong> decide qué partes de tu perfil son públicas.</li>
                      <li style={liStyle}><strong>Ubicación:</strong> se usa para mostrar resultados cercanos y ayudarte al publicar.</li>
                    </ul>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      <HelpCTA label="Ir a Ajustes" onClick={() => go('ajustes')} />
                      <HelpCTA label="Legal" kind="ghost" onClick={() => go('legal', { tipo: 'privacidad' })} />
                    </div>
                  </>
                ) : null}

                {id === 'comunidad' ? (
                  <>
                    <div style={hStyle}>Qué encontrarás</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>Publicaciones:</strong> eventos, lugares y contenido de la comunidad.</li>
                      <li style={liStyle}><strong>Sorteos oficiales:</strong> aparecen en la parte superior del feed cuando están activos.</li>
                      <li style={liStyle}><strong>Guardar:</strong> puedes guardar publicaciones para verlas luego.</li>
                    </ul>
                    <div style={hStyle}>Publicar en Comunidad</div>
                    <p style={{ margin: 0 }}>
                      Para publicar normalmente necesitas <strong>email</strong> y <strong>teléfono</strong> verificados. Si te aparece un bloqueo, entra en Ajustes → Cuenta.
                    </p>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      <HelpCTA label="Ir a Comunidad" onClick={() => go('comunidad')} />
                      <HelpCTA label="Ajustes → Cuenta" kind="ghost" onClick={() => go('ajustes', { initialSub: 'cuenta' })} />
                    </div>
                  </>
                ) : null}

                {id === 'subastas' ? (
                  <>
                    <div style={hStyle}>Estado</div>
                    <p style={{ margin: 0 }}>
                      Las subastas se habilitarán en fases posteriores. Cuando estén disponibles, aquí tendrás guía para pujar y para “comprar ya”.
                    </p>
                  </>
                ) : null}

                {id === 'partners' ? (
                  <>
                    <div style={hStyle}>Qué significa “Partner verificado”</div>
                    <p style={{ margin: 0 }}>
                      Un partner verificado es una tienda o punto asociado. Verás su distintivo y su ficha pública.
                    </p>
                    <div style={hStyle}>En esta fase</div>
                    <p style={{ margin: 0 }}>
                      La logística avanzada con partners se habilitará más adelante. Si ves opciones relacionadas, pueden estar en preparación.
                    </p>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      <HelpCTA label="Solicitar Partner" onClick={() => go('registro-tienda')} />
                    </div>
                  </>
                ) : null}

                {id === 'noticias' ? (
                  <>
                    <div style={hStyle}>Consejos</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>Leer y compartir:</strong> puedes abrir una noticia y compartirla (enlace o copiar).</li>
                      <li style={liStyle}><strong>Me gusta:</strong> si tienes sesión se guarda en tu cuenta; como invitado puede guardarse en el navegador.</li>
                      <li style={liStyle}><strong>Boletín:</strong> si ves el formulario de novedades, puedes suscribirte para recibir correos.</li>
                    </ul>
                    <div style={hStyle}>Preguntas frecuentes</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>¿Puedo leer noticias sin cuenta?</strong> Sí, una vez dentro de la beta.</li>
                      <li style={liStyle}><strong>¿Cómo comparto una noticia?</strong> Abre la noticia y usa el botón de compartir o copia el enlace.</li>
                    </ul>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                      <HelpCTA label="Ir a Noticias" onClick={() => go('noticias')} />
                    </div>
                  </>
                ) : null}

                {id === 'perfil-vendedor' ? (
                  <>
                    <div style={hStyle}>Qué verás</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>Anuncios en venta</strong> del vendedor.</li>
                      <li style={liStyle}><strong>Valoraciones</strong> (cuando estén activas por compras).</li>
                      <li style={liStyle}><strong>Contacto por chat</strong> desde su perfil o desde un anuncio.</li>
                    </ul>
                    <p style={pMuted}>Consejo: si un anuncio te interesa, pregunta por estado, edición y entrega.</p>
                  </>
                ) : null}

                {id === 'soporte' ? (
                  <>
                    <div style={hStyle}>Reportar un problema</div>
                    <ul style={ulStyle}>
                      <li style={liStyle}><strong>Botón de reporte/ayuda:</strong> si lo ves en pantalla, úsalo para enviar un error o sugerencia.</li>
                      <li style={liStyle}><strong>Qué incluir:</strong> qué estabas intentando hacer, en qué pantalla ocurrió y (si puedes) una captura.</li>
                    </ul>
                    <div style={hStyle}>Contacto</div>
                    <p style={{ margin: 0 }}>
                      Escríbenos a{' '}
                      <a href="mailto:contacto@dreamvinyl.es" style={{ color: C.gold, fontWeight: 900 }}>
                        contacto@dreamvinyl.es
                      </a>
                      .
                    </p>
                  </>
                ) : null}
              </div>
            </details>
          ))}
        </div>

        <div style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <div style={{ fontWeight: 900, color: '#fff', marginBottom: 6 }}>¿Necesitas ayuda del equipo?</div>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.86rem', lineHeight: 1.6 }}>
            Escríbenos a{' '}
            <a href="mailto:contacto@dreamvinyl.es" style={{ color: C.gold, fontWeight: 900 }}>
              contacto@dreamvinyl.es
            </a>{' '}
            y cuéntanos qué estabas intentando hacer (si puedes, adjunta una captura).
          </p>
        </div>
      </article>
    </div>
  );
}
